import ReactSelect, {
  Props,
  components,
  ClearIndicatorProps,
} from 'react-select'
import { cn } from '../UI/cn'
import React, { forwardRef } from 'react'
import { PiCaretDown } from 'react-icons/pi'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'
import { colors } from '../../styles/tokens'
import { BsXCircleFill } from 'react-icons/bs'

// Custom clear indicator to match FilterCloseButton styling
const CustomClearIndicator = (
  props: ClearIndicatorProps<any, boolean, any>
) => {
  return (
    <components.ClearIndicator {...props}>
      <BsXCircleFill className="w-[13px] h-[13px] text-fg-muted cursor-pointer" />
    </components.ClearIndicator>
  )
}

// DS2026 caret — Phosphor chevron, replacing react-select's built-in SVG.
const Ds2026DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <PiCaretDown size={16} color={colors['neutral-500']} />
  </components.DropdownIndicator>
)

// Default ``minHeight`` applied to the react-select control. Exported so
// callers that need an input visually matching this control (e.g. the
// search box in ProductSearchModal) can stay in sync without duplicating
// the literal.
export const REACT_SELECT_DEFAULT_CONTROL_HEIGHT = '33.5px'

// ``height`` is a wrapper-only addition that overrides the default
// ``minHeight`` on the control. Other props pass through to
// react-select unchanged.
type WrappedProps = Props & { height?: string; ds2026?: boolean }

const ReactSelectWrapper = forwardRef<any, WrappedProps>((props, ref) => {
  // Strip ``height`` / ``ds2026`` from the props that get spread onto
  // react-select — they're wrapper-only additions and react-select would warn
  // about unknown HTML props.
  const { height, ds2026, ...rest } = props as WrappedProps
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  return (
    <ReactSelect
      menuPortalTarget={document.body}
      closeMenuOnScroll={false}
      blurInputOnSelect={!rest.isMulti}
      captureMenuScroll={false}
      menuShouldBlockScroll={false}
      isOptionDisabled={(option: any) => option.disabled}
      components={{
        ClearIndicator: CustomClearIndicator,
        ...(ds2026 ? { DropdownIndicator: Ds2026DropdownIndicator } : {}),
        ...rest.components,
      }}
      {...rest}
      styles={{
        ...rest.styles,
        menuPortal: (provided) => ({
          ...provided,
          zIndex,
          pointerEvents: 'all',
        }),
        control: (provided, state) => ({
          // ...provided,
          ...rest.styles?.control?.(provided, state),
          display: 'flex',
          minHeight: height ?? REACT_SELECT_DEFAULT_CONTROL_HEIGHT,
        }),
        placeholder: (provided, state) => ({
          ...provided,
          ...rest.styles?.placeholder?.(provided, state),
          color: undefined,
        }),
      }}
      classNames={{
        ...rest.classNames,
        control: (provided) => {
          return cn(
            'form-control-select',
            {
              'form-control-select-focus': provided.isFocused,
              'opacity-50': rest.isDisabled,
            },
            rest.classNames?.control?.(provided) ?? ''
          )
        },
        placeholder: (provided) =>
          'form-control-select-placeholder' +
          (rest.classNames?.placeholder?.(provided) ?? ''),
        // DS2026 drops the pistachio hover/selected classes (!important) so the
        // inline neutral-100 option style wins; legacy keeps them.
        option: (provided) =>
          (ds2026
            ? ''
            : `${provided.isFocused ? 'hover-menu-item' : ''} ${
                provided.isSelected ? 'selected-menu-item' : ''
              }`) + (rest.classNames?.option?.(provided) ?? ''),
      }}
      ref={ref}
    />
  )
})

ReactSelectWrapper.displayName = 'ReactSelectWrapper'

export default ReactSelectWrapper
